import find from 'lodash/find';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { useAuth0 } from '@/auth';
import {
  AttendeeStatus,
  BookingsDocument,
  EventDocument,
  EventFieldsFragment,
  useEventQuery,
  useSignupForEventAgainMutation,
  useSignupForEventMutation,
} from '@/generated/graphql';
import { useCurrentUser } from '@/hooks/use-current-user/use-current-user';
const findEventAttendee = (event?: EventFieldsFragment, userId?: string) =>
  find(event?.attendees?.edges, ['node.user.id', userId])?.node;

export const useJoinEvent = () => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const router = useRouter();
  const { data: user } = useCurrentUser();
  const userId = user?.id as string | undefined;

  const { refetch: fetchEvent } = useEventQuery({
    skip: true,
  });

  const [signupForEvent, { loading: loadingCreate }] = useSignupForEventMutation({
    refetchQueries: [EventDocument, BookingsDocument],
  });

  const [signupForEventAgain, { loading: loadingUpdate }] = useSignupForEventAgainMutation({
    refetchQueries: [EventDocument, BookingsDocument],
  });

  const loading = loadingCreate || loadingUpdate;

  const joinEvent = useCallback(
    async (eventId: string) => {
      const event = (await fetchEvent({ id: eventId })).data?.event;
      if (!event) {
        return;
      }

      if (!isAuthenticated) {
        loginWithRedirect({ screen_hint: 'signup' });
        return;
      }

      const attendee = findEventAttendee(event, userId);
      const attendeeId = attendee?.id;
      const isAttendee = userId && attendee && attendee.status === AttendeeStatus.Confirmed;
      if (isAttendee) {
        router.push(`/bookings/${eventId}`);
        return;
      }
      if (!userId || !eventId) {
        return;
      }

      await (attendeeId
        ? signupForEventAgain({
            variables: {
              attendeeId,
            },
          })
        : signupForEvent({
            variables: {
              userId,
              eventId,
            },
          }));
      router.push(`/bookings/${eventId}`);
    },
    [
      isAuthenticated,
      fetchEvent,
      userId,
      signupForEventAgain,
      signupForEvent,
      router,
      loginWithRedirect,
    ],
  );

  return {
    joinEvent,
    loading,
  };
};
